import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle2, FileCheck2, FileSearch, FileUp, LoaderCircle, RefreshCw, ScanLine
} from "lucide-react";
import {
  Button, EmptyState, ErrorBanner, Field, Input, LoadingState, PageHeader, Panel, Select, StatusBadge
} from "../components/Ui";
import { useAuth } from "../context/AuthContext";
import { requireSupabase } from "../lib/supabase";
import { OrderLink } from "../components/OrderLink";
import { useDeveloperSettings } from "../lib/developerSettings";
import type { DepartmentRow, StorageLocationRow, SupplierRow, WarehouseRow } from "../types";

const ORDER_NUMBER = /^[0-9]{7}-[0-9]{3}$/;

type ScannerStatus = "review_required" | "ready" | "completed" | "failed" | "cancelled";

type ScannerDocument = {
  id: string;
  warehouse_id: string;
  storage_path: string;
  original_file_name: string;
  size_bytes: number;
  page_count: number | null;
  processing_status: ScannerStatus;
  ocr_status: "pending" | "processing" | "review_required" | "confirmed" | "failed";
  detected_order_numbers: string[];
  detected_supplier_names: string[];
  delivery_note_number: string | null;
  captured_at: string;
};

type WorkOrderRow = { id: string; order_number: string; department_id: string | null };

type ReceiptEntry = {
  orderNumber: string;
  departmentId: string;
  storageLocationId: string;
  packageCount: string;
  note: string;
  suggestion: string;
};

function parseOrderNumbers(value: string): { values: string[]; invalid: string[] } {
  const tokens = value.split(/[\s,;]+/).map((item) => item.trim()).filter(Boolean);
  const invalid = tokens.filter((item) => !ORDER_NUMBER.test(item));
  return { values: [...new Set(tokens.filter((item) => ORDER_NUMBER.test(item)))], invalid };
}

function safeFileName(value: string) {
  const withoutExtension = value.replace(/\.pdf$/i, "");
  const normalized = withoutExtension.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
  const safe = normalized.replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 180);
  return `${safe || "lieferschein"}.pdf`;
}

async function sha256(file: File) {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function formatDateTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("de-DE", {
    dateStyle: "short",
    timeStyle: "short"
  }).format(date);
}

function formatBytes(value: number) {
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} KB`;
  return `${(value / 1024 / 1024).toLocaleString("de-DE", { maximumFractionDigits: 1 })} MB`;
}

function warehouseLabelsForScanner(code: WarehouseRow["code"] | undefined) {
  if (code === "OB") return "Oberhausen";
  if (code === "MZ") return "Merzenich";
  return "Lager auswählen";
}

function statusPresentation(status: ScannerStatus) {
  if (status === "review_required") return { label: "Prüfung offen", tone: "warning" as const };
  if (status === "ready") return { label: "Bereit zum Einlagern", tone: "success" as const };
  if (status === "completed") return { label: "Übernommen", tone: "neutral" as const };
  if (status === "failed") return { label: "Fehler", tone: "danger" as const };
  return { label: "Verworfen", tone: "neutral" as const };
}

export function ScannerInboxPage() {
  const { cloudConfigured, profile } = useAuth();
  const developerSettings = useDeveloperSettings();
  const fileInput = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<ScannerDocument[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseRow[]>([]);
  const [departments, setDepartments] = useState<DepartmentRow[]>([]);
  const [locations, setLocations] = useState<StorageLocationRow[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrderRow[]>([]);
  const [warehouseId, setWarehouseId] = useState(profile?.warehouse_id ?? "");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(cloudConfigured);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selected, setSelected] = useState<ScannerDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [orderText, setOrderText] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [deliveryNoteNumber, setDeliveryNoteNumber] = useState("");
  const [pageCount, setPageCount] = useState("1");
  const [entries, setEntries] = useState<ReceiptEntry[]>([]);

  const accessibleWarehouses = useMemo(() => profile?.warehouse_id
    ? warehouses.filter((warehouse) => warehouse.id === profile.warehouse_id)
    : warehouses, [profile?.warehouse_id, warehouses]);
  const selectedLocations = useMemo(() => locations.filter((location) => location.warehouse_id === (selected?.warehouse_id ?? warehouseId)), [locations, selected?.warehouse_id, warehouseId]);

  const load = useCallback(async () => {
    if (!cloudConfigured) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const client = requireSupabase();
    const [documentResult, warehouseResult, departmentResult, locationResult, supplierResult, orderResult] = await Promise.all([
      client.from("delivery_note_documents")
        .select("id,warehouse_id,storage_path,original_file_name,size_bytes,page_count,processing_status,ocr_status,detected_order_numbers,detected_supplier_names,delivery_note_number,captured_at")
        .eq("document_source", "network_scanner")
        .order("captured_at", { ascending: false }),
      client.from("warehouses").select("id,code,name,is_active").eq("is_active", true).order("name"),
      client.from("departments").select("id,code,name,is_active").eq("is_active", true).order("name"),
      client.from("storage_locations").select("id,warehouse_id,code,description,is_active").eq("is_active", true).order("code"),
      client.from("suppliers").select("id,name,is_active").eq("is_active", true).order("name"),
      client.from("work_orders").select("id,order_number,department_id")
    ]);
    const firstError = documentResult.error || warehouseResult.error || departmentResult.error
      || locationResult.error || supplierResult.error || orderResult.error;
    if (firstError) {
      setError(`Scanner-Eingang konnte nicht geladen werden: ${firstError.message}`);
      setLoading(false);
      return;
    }
    const nextWarehouses = (warehouseResult.data ?? []) as WarehouseRow[];
    setDocuments((documentResult.data ?? []) as ScannerDocument[]);
    setWarehouses(nextWarehouses);
    setDepartments((departmentResult.data ?? []) as DepartmentRow[]);
    setLocations((locationResult.data ?? []) as StorageLocationRow[]);
    setSuppliers((supplierResult.data ?? []) as SupplierRow[]);
    setWorkOrders((orderResult.data ?? []) as WorkOrderRow[]);
    setWarehouseId((current) => current || profile?.warehouse_id || nextWarehouses[0]?.id || "");
    setError("");
    setLoading(false);
  }, [cloudConfigured, profile?.warehouse_id]);

  useEffect(() => { void load(); }, [load]);
  const selectedWarehouseCode = warehouses.find((warehouse) => warehouse.id === warehouseId)?.code;
  const scannerProfile = selectedWarehouseCode ? developerSettings.scanners[selectedWarehouseCode] : null;
  const scannerAvailable = developerSettings.workflow.scannerEnabled && (scannerProfile?.enabled ?? true);
  const maxPdfBytes = developerSettings.documents.maxFileSizeMb * 1024 * 1024;

  const prepareEntries = useCallback(async (orderNumbers: string[], targetWarehouseId: string) => {
    const knownOrders = new Map(workOrders.map((order) => [order.order_number, order]));
    const defaults: ReceiptEntry[] = orderNumbers.map((orderNumber) => ({
      orderNumber,
      departmentId: knownOrders.get(orderNumber)?.department_id ?? departments[0]?.id ?? "",
      storageLocationId: locations.find((location) => location.warehouse_id === targetWarehouseId)?.id ?? "",
      packageCount: "1",
      note: "",
      suggestion: "Kein früherer Lagerplatz gefunden."
    }));
    setEntries(defaults);

    const client = requireSupabase();
    const suggestions = await Promise.all(orderNumbers.map(async (orderNumber) => {
      const { data } = await client.rpc("suggest_storage_location", {
        target_order_number: orderNumber,
        target_warehouse_id: targetWarehouseId
      });
      const row = Array.isArray(data) ? data[0] : null;
      return row ? { orderNumber, id: String(row.storage_location_id), code: String(row.storage_location_code) } : null;
    }));
    setEntries((current) => current.map((entry) => {
      const suggestion = suggestions.find((item) => item?.orderNumber === entry.orderNumber);
      return suggestion ? {
        ...entry,
        storageLocationId: suggestion.id,
        suggestion: `Letzter bekannter Lagerplatz ${suggestion.code} wurde vorgeschlagen.`
      } : entry;
    }));
  }, [departments, locations, workOrders]);

  const selectDocument = useCallback(async (document: ScannerDocument) => {
    setSelected(document);
    setOrderText(document.detected_order_numbers.join("\n"));
    setSupplierName(document.detected_supplier_names[0] ?? "");
    setDeliveryNoteNumber(document.delivery_note_number ?? "");
    setPageCount(String(document.page_count ?? 1));
    setEntries([]);
    setError("");
    const client = requireSupabase();
    const { data, error: signedUrlError } = await client.storage.from("delivery-notes").createSignedUrl(document.storage_path, 600);
    if (signedUrlError) setError(`PDF-Vorschau konnte nicht geöffnet werden: ${signedUrlError.message}`);
    setPreviewUrl(data?.signedUrl ?? "");
    if (document.processing_status === "ready" && document.detected_order_numbers.length > 0) {
      await prepareEntries(document.detected_order_numbers, document.warehouse_id);
    }
  }, [prepareEntries]);

  async function uploadPdf() {
    if (!cloudConfigured) {
      setError("Der Scanner-Eingang benötigt die verbundene Supabase-Testcloud.");
      return;
    }
    if (!file || !warehouseId) {
      setError("Bitte Lager und PDF auswählen.");
      return;
    }
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setError("Der Scanner-Eingang akzeptiert ausschließlich PDF-Dateien.");
      return;
    }
    if (!scannerAvailable) {
      setError("Der Scanner-Eingang ist für dieses Lager in den Entwicklereinstellungen deaktiviert.");
      return;
    }
    if (file.size < 1 || file.size > maxPdfBytes) {
      setError(`Die PDF muss zwischen 1 Byte und ${developerSettings.documents.maxFileSizeMb} MB groß sein.`);
      return;
    }

    setBusy(true);
    setError("");
    setSuccess("");
    const client = requireSupabase();
    const documentId = crypto.randomUUID();
    const storagePath = `${warehouseId}/scanner-inbox/${documentId}/${safeFileName(file.name)}`;
    try {
      const checksum = await sha256(file);
      const uploadResult = await client.storage.from("delivery-notes").upload(storagePath, file, {
        contentType: "application/pdf",
        upsert: false
      });
      if (uploadResult.error) throw uploadResult.error;

      const { error: registerError } = await client.rpc("register_scanner_document", {
        target_document_id: documentId,
        target_warehouse_id: warehouseId,
        target_storage_path: storagePath,
        target_original_file_name: file.name,
        target_mime_type: "application/pdf",
        target_size_bytes: file.size,
        target_checksum_sha256: checksum
      });
      if (registerError) {
        await client.storage.from("delivery-notes").remove([storagePath]);
        throw registerError;
      }

      setSuccess(`${file.name} wurde privat in den Scanner-Eingang hochgeladen.`);
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      await load();
      const uploaded = (await client.from("delivery_note_documents")
        .select("id,warehouse_id,storage_path,original_file_name,size_bytes,page_count,processing_status,ocr_status,detected_order_numbers,detected_supplier_names,delivery_note_number,captured_at")
        .eq("id", documentId).single()).data as ScannerDocument | null;
      if (uploaded) await selectDocument(uploaded);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Die PDF konnte nicht hochgeladen werden.");
    } finally {
      setBusy(false);
    }
  }

  async function saveReview() {
    if (!selected) return;
    const parsed = parseOrderNumbers(orderText);
    const normalizedSupplier = supplierName.trim();
    const normalizedPageCount = Number(pageCount);
    if (parsed.invalid.length > 0 || parsed.values.length === 0) {
      setError(parsed.invalid.length > 0
        ? `Ungültige Auftragsnummern: ${parsed.invalid.join(", ")}`
        : "Mindestens eine K&S-Auftragsnummer im Format 1234567-001 ist erforderlich.");
      return;
    }
    if (normalizedSupplier.length < 2) {
      setError("Bitte den Lieferanten vollständig bestätigen.");
      return;
    }
    if (!Number.isInteger(normalizedPageCount) || normalizedPageCount < 1 || normalizedPageCount > 100) {
      setError("Die Seitenzahl muss zwischen 1 und 100 liegen.");
      return;
    }

    setBusy(true);
    setError("");
    const client = requireSupabase();
    const { error: reviewError } = await client.rpc("save_scanner_document_review", {
      target_document_id: selected.id,
      target_order_numbers: parsed.values,
      target_supplier_names: [normalizedSupplier],
      target_delivery_note_number: deliveryNoteNumber.trim() || null,
      target_page_count: normalizedPageCount,
      target_ocr_payload: {
        engine: "manual-scanner-review",
        recognized_order_numbers: parsed.values,
        recognized_suppliers: [normalizedSupplier],
        reviewed_by_user: true
      }
    });
    if (reviewError) {
      setError(reviewError.message);
      setBusy(false);
      return;
    }
    const reviewed: ScannerDocument = {
      ...selected,
      detected_order_numbers: parsed.values,
      detected_supplier_names: [normalizedSupplier],
      delivery_note_number: deliveryNoteNumber.trim() || null,
      page_count: normalizedPageCount,
      processing_status: "ready",
      ocr_status: "confirmed"
    };
    setSelected(reviewed);
    setDocuments((current) => current.map((item) => item.id === reviewed.id ? reviewed : item));
    await prepareEntries(parsed.values, selected.warehouse_id);
    setSuccess("Dokumentdaten bestätigt. Jetzt Lagerplätze und Packstückzahlen zuordnen.");
    setBusy(false);
  }

  function updateEntry(orderNumber: string, patch: Partial<ReceiptEntry>) {
    setEntries((current) => current.map((entry) => entry.orderNumber === orderNumber ? { ...entry, ...patch } : entry));
  }

  async function finalizeDocument() {
    if (!selected) return;
    if (entries.length !== selected.detected_order_numbers.length) {
      setError("Für jede bestätigte Auftragsnummer ist eine Einlagerungszeile erforderlich.");
      return;
    }
    if (entries.some((entry) => !entry.departmentId || !entry.storageLocationId
      || !Number.isInteger(Number(entry.packageCount)) || Number(entry.packageCount) < 1 || Number(entry.packageCount) > 200)) {
      setError("Bitte Abteilung, Lagerplatz und Packstückzahl für jeden Auftrag vollständig prüfen.");
      return;
    }

    setBusy(true);
    setError("");
    const client = requireSupabase();
    const { data, error: finalizeError } = await client.rpc("finalize_scanner_document", {
      target_document_id: selected.id,
      target_supplier_name: supplierName.trim(),
      target_delivery_note_number: deliveryNoteNumber.trim() || null,
      target_entries: entries.map((entry) => ({
        order_number: entry.orderNumber,
        department_id: entry.departmentId,
        storage_location_id: entry.storageLocationId,
        package_count: Number(entry.packageCount),
        note: entry.note.trim() || null
      }))
    });
    if (finalizeError) {
      setError(finalizeError.message);
      setBusy(false);
      return;
    }
    const packageCount = Array.isArray(data) ? data.length : 0;
    setSuccess(`Einlagerung abgeschlossen. ${packageCount} Etikett${packageCount === 1 ? "" : "en"} wurden in die Druckwarteschlange gestellt.`);
    setSelected(null);
    setPreviewUrl("");
    setEntries([]);
    await load();
    setBusy(false);
  }

  return (
    <>
      <PageHeader
        title="Scanner-Eingang"
        description="Mehrseitige Netzwerk-Scans einmal privat hochladen und anschließend am PC oder Lagergerät weiterbearbeiten."
        action={<Button variant="secondary" onClick={() => void load()} disabled={loading || busy}><RefreshCw size={16} /> Aktualisieren</Button>}
      />
      {!cloudConfigured && <ErrorBanner message="Der Scanner-Eingang ist im lokalen Vorschaumodus deaktiviert. Bitte die Supabase-Testcloud verbinden." />}
      {error && <ErrorBanner message={error} />}
      {success && <div className="success-banner"><CheckCircle2 size={17} /><span>{success}</span></div>}
      {scannerProfile && <div className="scanner-profile-banner"><ScanLine size={18} /><span><strong>{scannerProfile.model || "Scanner noch nicht festgelegt"}</strong><small>{warehouseLabelsForScanner(selectedWarehouseCode)}{scannerProfile.networkAddress ? ` · ${scannerProfile.networkAddress}` : ""}{scannerProfile.sourceFolder ? ` · Ordner ${scannerProfile.sourceFolder}` : ""} · {scannerProfile.dpi} dpi · {scannerProfile.duplex ? "Duplex" : "Einseitig"}</small></span><StatusBadge tone={scannerAvailable ? "success" : "warning"}>{scannerAvailable ? "Aktiv" : "Deaktiviert"}</StatusBadge></div>}

      <Panel title="PDF vom Netzwerkscanner übernehmen" description="Der Scanner erzeugt eine zusammenhängende PDF. Das Original bleibt im privaten Lieferschein-Bucket.">
        <div className="scanner-upload-form">
          <Field label="Lager">
            <Select value={warehouseId} disabled={Boolean(profile?.warehouse_id) || busy} onChange={(event) => setWarehouseId(event.target.value)}>
              <option value="">Lager auswählen</option>
              {accessibleWarehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
            </Select>
          </Field>
          <Field label="Mehrseitige PDF" hint={`PDF, höchstens ${developerSettings.documents.maxFileSizeMb} MB`}>
            <input ref={fileInput} className="input" type="file" accept="application/pdf,.pdf" disabled={!cloudConfigured || busy || !scannerAvailable} onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
          </Field>
          <Button onClick={() => void uploadPdf()} disabled={!cloudConfigured || busy || !file || !warehouseId || !scannerAvailable}>
            {busy ? <LoaderCircle className="spin" size={16} /> : <FileUp size={16} />} PDF hochladen
          </Button>
        </div>
      </Panel>

      <div className="scanner-workspace">
        <Panel title="Offene Scanner-Dokumente" description="OCR-Ergebnisse werden erst nach Sichtkontrolle übernommen.">
          {loading ? <LoadingState label="Scanner-Eingang wird geladen …" /> : documents.length === 0 ? (
            <EmptyState title="Noch keine Scanner-PDF vorhanden" description="Die nächste hochgeladene PDF erscheint hier lagerbezogen." />
          ) : <div className="scanner-document-list">{documents.map((document) => {
            const warehouse = warehouses.find((item) => item.id === document.warehouse_id);
            const hasAutomaticSuggestion = document.ocr_status === "review_required"
              && (document.detected_order_numbers.length > 0 || document.detected_supplier_names.length > 0);
            const presentation = hasAutomaticSuggestion
              ? { label: "OCR-Vorschlag prüfen", tone: "warning" as const }
              : statusPresentation(document.processing_status);
            return <button type="button" key={document.id} className={`scanner-document-row ${selected?.id === document.id ? "scanner-document-row--active" : ""}`} onClick={() => void selectDocument(document)}>
              <span className="scanner-document-row__icon"><FileSearch size={19} /></span>
              <span><strong>{document.original_file_name}</strong><small>{warehouse?.name ?? "Lager"} · {formatBytes(document.size_bytes)} · {formatDateTime(document.captured_at)}</small></span>
              <StatusBadge tone={presentation.tone}>{presentation.label}</StatusBadge>
            </button>;
          })}</div>}
        </Panel>

        <Panel title={selected ? "Lieferschein prüfen" : "Dokument auswählen"} description={selected ? "Alle Seiten und vorgeschlagenen Angaben vollständig kontrollieren." : "Links ein Scanner-Dokument auswählen."}>
          {!selected ? <EmptyState title="Keine PDF ausgewählt" description="Wähle links ein Dokument zur Sichtkontrolle aus." /> : (
            <div className="scanner-review">
              {previewUrl ? <iframe className="scanner-preview" title={`Vorschau ${selected.original_file_name}`} src={previewUrl} /> : <LoadingState label="Private PDF-Vorschau wird vorbereitet …" />}
              {selected.processing_status === "completed" ? (
                <div className="scanner-completed"><FileCheck2 size={22} /><strong>Bereits in Wareneingänge übernommen</strong><p>Das Original bleibt einmal gespeichert und ist mit allen zugehörigen Aufträgen verknüpft.</p></div>
              ) : <>
                {selected.ocr_status === "review_required" && (selected.detected_order_numbers.length > 0 || selected.detected_supplier_names.length > 0) && (
                  <div className="notice-banner system-notice"><strong>Automatisch erkannt.</strong>&nbsp; Auftragsnummer, Lieferant und Seitenzahl wurden vom Scanner-PC vorgeschlagen und müssen vor der Einlagerung bestätigt werden.</div>
                )}
                <div className="scanner-review-grid">
                  <Field label="K&S-Auftragsnummern" hint="Eine Nummer pro Zeile; mehrere Nummern sind möglich.">
                    <textarea className="input scanner-textarea" value={orderText} onChange={(event) => setOrderText(event.target.value)} placeholder={developerSettings.exampleTexts.orderNumber} />
                  </Field>
                  <Field label="Lieferant">
                    <Input list="scanner-suppliers" value={supplierName} onChange={(event) => setSupplierName(event.target.value)} placeholder={developerSettings.exampleTexts.supplier} />
                  </Field>
                  <Field label="Lieferscheinnummer">
                    <Input value={deliveryNoteNumber} onChange={(event) => setDeliveryNoteNumber(event.target.value)} placeholder={developerSettings.exampleTexts.optional} />
                  </Field>
                  <Field label="Seitenzahl">
                    <Input type="number" min="1" max="100" value={pageCount} onChange={(event) => setPageCount(event.target.value)} />
                  </Field>
                </div>
                <datalist id="scanner-suppliers">{suppliers.map((supplier) => <option key={supplier.id} value={supplier.name} />)}</datalist>
                <div className="scanner-review-actions">
                  <Button onClick={() => void saveReview()} disabled={busy}><ScanLine size={16} /> Angaben bestätigen</Button>
                </div>

                {selected.processing_status === "ready" && entries.length > 0 && <div className="scanner-allocation">
                  <header><h3>Aufträge einlagern</h3><p>Packstücke werden immer vollständig angelegt. Bei mehreren K&S-Nummern erfolgt die Zuordnung getrennt.</p></header>
                  {entries.map((entry) => <section className="scanner-order-card" key={entry.orderNumber}>
                    <strong><OrderLink orderNumber={entry.orderNumber} /></strong>
                    <small>{entry.suggestion}</small>
                    <div className="scanner-order-fields">
                      <Field label="Abteilung">
                        <Select value={entry.departmentId} onChange={(event) => updateEntry(entry.orderNumber, { departmentId: event.target.value })}>
                          <option value="">Auswählen</option>
                          {departments.map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
                        </Select>
                      </Field>
                      <Field label="Lagerplatz">
                        <Select value={entry.storageLocationId} onChange={(event) => updateEntry(entry.orderNumber, { storageLocationId: event.target.value })}>
                          <option value="">Auswählen</option>
                          {selectedLocations.map((location) => <option key={location.id} value={location.id}>{location.code}</option>)}
                        </Select>
                      </Field>
                      <Field label="Packstücke">
                        <Input type="number" min="1" max="200" value={entry.packageCount} onChange={(event) => updateEntry(entry.orderNumber, { packageCount: event.target.value })} />
                      </Field>
                      <Field label="Notiz">
                        <Input value={entry.note} onChange={(event) => updateEntry(entry.orderNumber, { note: event.target.value })} placeholder={developerSettings.exampleTexts.optional} />
                      </Field>
                    </div>
                  </section>)}
                  <Button onClick={() => void finalizeDocument()} disabled={busy}><FileCheck2 size={16} /> Einlagerung verbindlich abschließen</Button>
                </div>}
              </>}
            </div>
          )}
        </Panel>
      </div>
    </>
  );
}
